import { RefObject } from "react";

import { Client } from "@/Common/Api/Client";
import { SearchIndividualResponse } from "@/Common/Api/Individuals/Dto/SearchIndividualResponse";
import { SearchIndividualResult } from "@/Common/Api/Individuals/Dto/SearchIndividualResult";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PortalStatusIcon } from "@/Common/Components/Portal/PortalStatusIcon";
import { IndividualSearchResultCachedPromise } from "@/Common/Components/Search/IndividualAdvancedSearchControl";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { RAMQInfo, RamqStatus } from "@/Common/Models/RAMQ/RAMQInfo";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LoadingIndicator } from "../Loader/LoadingIndicator";
import { StatusComponent } from "../Patient/StatusComponent";

import img_loading from "@/Globals/_images/loading.gif";
import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";

interface IPatientSelectCallbackFunction {
    (selectedperson: Person, text?: string): void;
}

interface ICustomSearchResultRenderFunction {
    (p: Person, patientDivClassName: string, idx: number, selectFunction: (p: Person) => void): JSX.Element;
}

/**
 * Configures the PatientAdvancedSearchControl. Each option affects how searches are performed, rendered, or reported to the parent.
 */
interface IPatientAdvancedSearchControlProps {
    /** Unique id applied to the root div. Required for hosting styles and automation hooks. */
    componentId: string;
    /** Optional extra CSS class names appended to the wrapper. */
    cssClass?: string;

    /** When true the control triggers a window resize shortly after mount/unmount to let surrounding layouts adjust height. */
    isAutoResize?: boolean;
    /** When true the results list renders but suppresses click handling (adds disabled styling and prevents selection). */
    isClickOnResultDisabled?: boolean;
    /** When true the search query requests inactive patients as well; changing this flag clears cached results and triggers a fresh search. */
    isIncludingInactivePatients?: boolean;
    /** Adds sidebar scroll styling so the results list gains a fixed-height scrolling container. */
    isScrollBarActive?: boolean;
    /** Focuses the internal textbox on mount; ignored when the textbox is hidden. */
    isFocusOnLoad?: boolean;
    /** Controls whether the built-in textbox renders. When false, callers must update defaultTextValue to keep the search in sync. */
    isRenderTextbox?: boolean;
    /** Drives whether the control queries the portal proxy or the Myle patient search endpoints. Changing it clears current results. */
    isSearchingPortal?: boolean;
    /** When portal search is active, include already-linked portal patients in results. */
    isPortalSearchIncludeAlreadyLinked?: boolean;
    /** Hides patient images when true to give a compact list layout. */
    hidePatientImage?: boolean;
    /** When true and patient edit is enabled, renders a "Create patient file" link beneath the textbox. */
    isCreatePatientLinkEnable?: boolean;

    /** Optional React key forwarded to the root element. */
    key?: string;
    /** Supplies an hvar data attribute used by legacy hover scripts. */
    hvar?: string;
    /** Inline styles applied to the results container, letting parents tweak sizing. */
    resultCSS?: React.CSSProperties;

    specificContactIdsToDisplay?: string[]; // when we want the results to only display specific patients (e.g. for displaying duplicate patient file)
    /** Maximum number of results requested from the server per search. */
    maxResultsToReturn: number
    /** Starting search text shown in the textbox (when rendered) and used for the initial fetch. Updating it triggers a new search. */
    defaultTextValue?: string;

    /** Custom renderer for Myle patient results; receives the Person, CSS class, index, and callback to invoke selection. */
    customMylePatientRenderFunction?: ICustomSearchResultRenderFunction;
    /** Custom renderer for portal patient results; same contract as the Myle renderer. */
    customPortalPatientRenderFunction?: ICustomSearchResultRenderFunction;

    /** Optional dispatcher operation id invoked on selection (skipped if selected person is still a light model). */
    onPatientSelectedOperation?: number;
    /** Callback fired after a selection succeeds, passing the chosen Person and the current search text. */
    onPatientSelectCallback?: IPatientSelectCallbackFunction;

    /** Notifies the parent with the count of results returned by the most recent search. */
    onPatientSearchResultAmountCallback?: (amount: number) => void;
    /** Invoked when the "Create patient file" link is clicked. Only rendered if the feature is enabled. */
    onAddPatientLinkClick?: () => void;
}

class PatientAdvancedSearchState {
    public searchTextValue: string;
    public isIncludingDeactivated: boolean;
    public isSearchingPortal: boolean;
    public searchPersonResults: Person[];
    public selectedPerson: Person;

    /**
     * A number incremented to force the control to re-render when asynchronous data changes.
     */
    public forceRenderId: number = 0;

    public static getDefault(props: IPatientAdvancedSearchControlProps): PatientAdvancedSearchState {
        const s = new PatientAdvancedSearchState();
        s.searchTextValue = props.defaultTextValue ?? "";
        s.isIncludingDeactivated = props.isIncludingInactivePatients;
        s.isSearchingPortal = props.isSearchingPortal;
        s.searchPersonResults = [];
        return s;
    }
}

class PatientAdvancedSearchControl extends BaseReactComponent<IPatientAdvancedSearchControlProps, PatientAdvancedSearchState> {
    private static readonly cacheTimeoutSeconds = 90;

    private validationHelper: ValidationHelper = new ValidationHelper();
    private fullPatientCacheDictionnary: { [contactId: string]: IndividualSearchResultCachedPromise<Person> } = {};
    private textInputRef: RefObject<HTMLInputElement> = React.createRef();
    private timeoutReference: number | undefined;
    private orderedResponseId: number = 0;
    private timeoutReferenceUIRefresh: number = -1;
    private patientRenderColumn2: React.CSSProperties = { justifyContent: "flex-start", width: "100%" };
    private patientRenderColumn2Div1: React.CSSProperties = { width: "50%" };
    private patientRenderColumn2Div2: React.CSSProperties = { width: "50%" };

    constructor(props: IPatientAdvancedSearchControlProps) {
        super(props);

        this.state = PatientAdvancedSearchState.getDefault(props);
    }

    public componentDidUpdate(prevProps: IPatientAdvancedSearchControlProps): void {
        const defaultValueChanged = this.props.defaultTextValue !== prevProps.defaultTextValue;
        const shouldResetList = (
            this.props.isIncludingInactivePatients !== prevProps.isIncludingInactivePatients
            || this.props.isSearchingPortal !== prevProps.isSearchingPortal
        );

        if (!defaultValueChanged && !shouldResetList)
            return;

        this.mutateState(
            s => {
                if (defaultValueChanged)
                    s.searchTextValue = this.props.defaultTextValue;

                if (shouldResetList) {
                    s.isSearchingPortal = this.props.isSearchingPortal;
                    s.isIncludingDeactivated = this.props.isIncludingInactivePatients;
                    s.searchPersonResults = [];
                }
            },
            () => this.invokeDebouncedSearch(this.state.searchTextValue)
        );
    }

    public componentDidMount(): void {
        if (this.props.isFocusOnLoad)
            this.textInputRef.current?.focus();

        if (!IsNullOrWhiteSpace(this.props.defaultTextValue) || this.props.specificContactIdsToDisplay?.any())
            this.invokeDebouncedSearch(this.props.defaultTextValue);

        this.adjustWindowScrollbar();
    }

    public adjustWindowScrollbar(): void {
        if (!this.props.isAutoResize)
            return;
        window.setTimeout(() => $(window).trigger("resize"), 150);
    }

    public shouldComponentUpdate(nextProps: IPatientAdvancedSearchControlProps, nextState: PatientAdvancedSearchState): boolean {
        return (
            this.props.defaultTextValue !== nextProps.defaultTextValue
            || this.props.isIncludingInactivePatients !== nextProps.isIncludingInactivePatients
            || this.props.isSearchingPortal !== nextProps.isSearchingPortal
            || this.state.searchPersonResults !== nextState.searchPersonResults
            || this.state.searchTextValue !== nextState.searchTextValue
            || this.state.selectedPerson !== nextState.selectedPerson
            || this.state.forceRenderId !== nextState.forceRenderId
        );
    }

    public componentWillUnmount(): void {
        this.adjustWindowScrollbar();
    }

    public render(): JSX.Element {
        var css = this.props.cssClass == null ? "" : this.props.cssClass + " ";
        var wrapperCss = this.state.searchPersonResults.length > 0 ? css + "pa-selected-items-list" : css + "pa-selected-items-list empty";
        var self = this;

        var scrollbarCss = "";
        if (this.props.isScrollBarActive) {
            scrollbarCss = "patient-sidebar-search-container fxheight";
        }

        var disabledSearchClass = "";

        if (this.props.isClickOnResultDisabled) {
            disabledSearchClass = " disabled-results";
        }

        var hvar = this.props.hvar == null ? "-52" : this.props.hvar;

        let resultCSS: React.CSSProperties = this.props.resultCSS == null ? {} : this.props.resultCSS;

        return (
            <div id={this.props.componentId} onClick={e => this.stopPropagation(e)} className={disabledSearchClass}>
                {this.props.isRenderTextbox && (
                    <div style={{ margin: "0 0 15px 0" }}>
                        <input onChange={e => this.searchTextChanged(e)} value={this.state.searchTextValue} type="text" className="form-control" ref={this.textInputRef} />
                        {!!this.props.isCreatePatientLinkEnable && cacheStore?.ClinicSettings.EnablePatientEdit && (
                            <div className="margin-sm-top margin-xs-bottom">
                                <a onClick={() => this.props.onAddPatientLinkClick?.()}>{cacheStore.GetString("CreatePatientFileLabel")}</a>
                            </div>
                        )}
                    </div>
                )}

                <div data-hvar={hvar} className={wrapperCss + " " + scrollbarCss} style={resultCSS}>
                    {this.state.searchPersonResults.map((p: Person, idx: number) => {
                        const isSelected =
                            self.state.selectedPerson
                            && (
                                p === self.state.selectedPerson
                                || (p.ContactId && p.ContactId === self.state.selectedPerson.ContactId)
                                || (!p.ContactId && p.PortalProfileId && p.PortalProfileId === self.state.selectedPerson.PortalProfileId)
                            );
                        const patientDivClassName = isSelected
                            ? "flex-container selectable selected"
                            : "flex-container selectable notselected";

                        if (!self.state.isSearchingPortal && p.Patient != null) {
                            if (self.props.customMylePatientRenderFunction != null) {
                                return self.props.customMylePatientRenderFunction(p, patientDivClassName, idx, p => this.onPatientSelect(p));
                            }
                            else {
                                return self.renderWithMylePatientInfo(p, patientDivClassName, idx);
                            }
                        }
                        else if (self.state.isSearchingPortal && p.PortalPatient != null) {
                            return self.renderWithPortalPatientInfo(p, patientDivClassName, idx);
                        }
                        else {
                            return self.renderWithoutPatientInfo(p, patientDivClassName, idx);
                        }
                    })}
                </div>

            </div>
        );
    }

    private renderWithoutPatientInfo(p: Person, patientDivClassName: string, idx: number): JSX.Element {
        return (
            <div className={patientDivClassName} onClick={() => this.onPatientSelect(p)} key={p.ContactId + "_" + idx}>

                {this.props.hidePatientImage != true && (
                    <div className="flex-col r1">
                        <img title={p.NameWithIdentifier} src={img_p_banner_icon_100} style={{ width: "48px" }} alt={p.NameWithIdentifier} />
                    </div>
                )}
                {this.props.hidePatientImage == true &&
                    <div style={{ width: "5px" }} />
                }

                <div className="flex-container" style={this.patientRenderColumn2}>
                    <div className="flex-col" style={this.patientRenderColumn2Div1}>
                        <h4 className="light-blue">{p.NameWithIdentifier}<span className="myle-icon-16 female" /></h4>
                        <ul>
                            <li><strong>{cacheStore.GetString("FileNumbersLabel")} </strong></li>
                            <li><span>...</span></li>
                            <li><span>...</span></li>
                        </ul>
                    </div>

                    <div className="flex-col" style={this.patientRenderColumn2Div2}>
                        <ul>
                            <li><LoadingIndicator imagePath={img_loading} /></li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    private renderWithMylePatientInfo(p: Person, patientDivClassName: string, idx: number): JSX.Element {
        let address = p.Patient.GetFormattedAddress();
        let nextApp = p.Patient.GetNextAppointmentFormatted();
        let dateOfBirth = p.Patient.GetFormattedDateOfBirth();
        let phone1 = p.Patient.GetFormattedPhonesByPriorityWithPosition(0);
        let phone2 = p.Patient.GetFormattedPhonesByPriorityWithPosition(1);
        let phone3 = p.Patient.GetFormattedPhonesByPriorityWithPosition(2);
        const isMale = p.Patient.Gender() === Gender.Male;
        const isFemale = p.Patient.Gender() === Gender.Female;
        const physicicanName = p.Patient.GetFormattedRamqDoctorOrStatus();

        let patientInfo = (
            <ul>
                {phone1 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone1}</span></li>}
                {phone2 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone2}</span></li>}
                {phone3 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone3}</span></li>}
                {address != "" && <li><span className="myle-icon-16 pin" /><span className="content-text">{address}</span></li>}
                {nextApp != "" && <li><span className="myle-icon-16 none" /><span className="content-text">{nextApp}</span></li>}
            </ul>
        );

        let self = this;

        var portalButton: JSX.Element = null;

        if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite) {
            const buttonProps = PortalHelper.getPortalButtonPropsForRegistrationState(p.PortalSyncState);
            portalButton = buttonProps &&
                <div style={{ marginTop: "5px" }}><PortalStatusIcon {...buttonProps} /></div>;
        }

        return (
            <div className={patientDivClassName} onClick={self.onPatientSelect.bind(self, p)} key={p.ContactId + "_" + idx}>

                {this.props.hidePatientImage != true && (
                    <div className="flex-col r1">
                        <img title={p.NameWithIdentifier} src={img_p_banner_icon_100} style={{ width: "48px" }} alt={p.NameWithIdentifier} />
                        {portalButton}
                    </div>
                )}
                {this.props.hidePatientImage == true &&
                    <div style={{ width: "5px" }} />
                }

                <div className="flex-container" style={this.patientRenderColumn2}>
                    <div className="flex-col" style={this.patientRenderColumn2Div1}>
                        <div className="display-flex-row-align-items-center">
                            <h4 className="light-blue">{p.Name}</h4>
                            <span style={{ marginLeft: ".3rem", marginTop: ".5rem" }}>{p.Patient.GetFormattedPreferredPronouns()}</span>
                            {isMale && <span className="myle-icon-16 male" />}
                            {isFemale && <span className="myle-icon-16 female" />}
                        </div>

                        <ul>
                            <li><strong>{sessionStore.getDefaultHealthCardLabel()} </strong><span>{p.GetFormattedNAM()} {p.Patient.GetFormattedNAMExpirationDateString()}</span></li>
                            <li><strong>{cacheStore.GetString("FileNumbersLabel")} </strong><span>{p.Patient.PatientResponse.Identifier01}</span></li>
                            <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong><span>{`${dateOfBirth} ${p.Patient.GetFormattedAge()}`}</span></li>
                            <li className="flex-container">
                                <strong>{cacheStore.GetString("PhysicianNameLabel")}</strong>
                                <span>
                                    <StatusComponent
                                        status={p.Patient?.GetAssignedDoctorStatus()}
                                        statusInfo={physicicanName}
                                    />
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div className="flex-col" style={this.patientRenderColumn2Div2}>
                        {patientInfo}
                    </div>
                </div>
            </div>
        );
    }

    private renderWithPortalPatientInfo(p: Person, patientDivClassName: string, idx: number): JSX.Element {
        let address1 = "";
        let dob = "";
        let formattedAge = "";
        let phone1 = "";
        let phone2 = "";
        let phone3 = "";
        let email = "";
        const isMale = p.gender === Gender.Male;
        const isFemale = p.gender === Gender.Female;

        if (p.PortalPatient.TelephoneNumbers != null && p.PortalPatient.TelephoneNumbers.length >= 1) {
            let formatted = PortalHelper.getFormattedPhoneNumber(p.PortalPatient.TelephoneNumbers[0], this.validationHelper);
            phone1 = formatted.FormattedPhoneNumber;
        }
        if (p.PortalPatient.TelephoneNumbers != null && p.PortalPatient.TelephoneNumbers.length >= 2) {
            let formatted = PortalHelper.getFormattedPhoneNumber(p.PortalPatient.TelephoneNumbers[1], this.validationHelper);
            phone2 = formatted.FormattedPhoneNumber;
        }
        if (p.PortalPatient.TelephoneNumbers != null && p.PortalPatient.TelephoneNumbers.length >= 3) {
            let formatted = PortalHelper.getFormattedPhoneNumber(p.PortalPatient.TelephoneNumbers[2], this.validationHelper);
            phone3 = formatted.FormattedPhoneNumber;
        }
        if (p.PortalPatient.MailingAddresses != null && p.PortalPatient.MailingAddresses.length >= 1) {
            let formatted = PortalHelper.getFormattedAddress(p.PortalPatient.MailingAddresses[0], this.validationHelper, true);
            address1 = formatted;
        }

        if (p.PortalPatient.DateOfBirth != null && p.PortalPatient.DateOfBirth.length > 0) {
            formattedAge = LuxonDateTimeParser.parse(p.PortalPatient.DateOfBirth).toFormat("yyyy-MM-dd");
        }

        if (p.PortalPatient.EmailAddresses != null && p.PortalPatient.EmailAddresses.length > 0) {
            email = p.PortalPatient.EmailAddresses[0].Email;
        }

        var patientInfo = (
            <ul>
                {phone1 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone1 + " " + this.toSafeString(phone1[1]) + " " + this.toSafeString(phone1[2])}</span></li>}
                {phone2 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone2 + " " + this.toSafeString(phone2[1]) + " " + this.toSafeString(phone2[2])}</span></li>}
                {phone3 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone3 + " " + this.toSafeString(phone3[1]) + " " + this.toSafeString(phone3[2])}</span></li>}
                {address1 != "" && <li><span className="myle-icon-16 pin" /><span className="content-text">{address1}</span></li>}
                {email != "" && <li title={email} style={{ maxWidth: "225px", overflow: "hidden", textOverflow: "ellipsis" }} className="content-text"><span style={{ overflow: "hidden", whiteSpace: "nowrap" }}> @ {email}</span></li>}
            </ul>
        );

        let self = this;

        var portalButton: JSX.Element = null;

        if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite) {
            const buttonProps = PortalHelper.getPortalButtonPropsForRegistrationState(p.PortalSyncState);
            portalButton = buttonProps &&
                <div><PortalStatusIcon {...buttonProps} /></div>;
        }

        return (
            <div className={patientDivClassName} onClick={self.onPatientSelect.bind(self, p)} key={p.ContactId + "_" + idx}>

                {this.props.hidePatientImage != true && (
                    <div className="flex-col r1">
                        <img title={p.NameWithIdentifier} src={img_p_banner_icon_100} style={{ width: "48px" }} alt={p.NameWithIdentifier} />
                        {portalButton}
                    </div>
                )}
                {this.props.hidePatientImage == true &&
                    <div style={{ width: "5px" }} />
                }

                <div className="flex-container" style={this.patientRenderColumn2}>
                    <div className="flex-col" style={this.patientRenderColumn2Div1}>
                        <div className="display-flex-row-align-items-center">
                            <h4 className="light-blue">{p.Name}</h4>
                            {isMale && <span className="myle-icon-16 male" />}
                            {isFemale && <span className="myle-icon-16 female" />}
                        </div>
                        <ul>
                            <li><strong>{sessionStore.getDefaultHealthCardLabel()} </strong><span>{p.GetFormattedNAM()}</span></li>
                            <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong><span>{dob + " (" + formattedAge + ") "}</span></li>
                        </ul>
                    </div>

                    <div className="flex-col" style={this.patientRenderColumn2Div2}>
                        {patientInfo}
                    </div>
                </div>

            </div>
        );
    }

    private onPatientSelect(selectedPerson: Person): void {
        if ((this.props.onPatientSelectedOperation || this.props.onPatientSelectCallback) && selectedPerson.isLightModel)
            return;

        this.mutateState(
            s => s.selectedPerson = selectedPerson,
            () => {
                if (this.props.onPatientSelectedOperation != null) {
                    sessionStore.dispatch(
                        this.props.onPatientSelectedOperation,
                        { "person": selectedPerson }
                    );
                }

                this.props.onPatientSelectCallback?.(selectedPerson, this.state.searchTextValue);
            }
        );
    }

    private stopPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private toSafeString(str: string): string {
        return str ?? "";
    }

    private searchTextChanged(e: React.ChangeEvent<HTMLInputElement>): void {
        var newText = e.target.value;
        this.mutateState(s => s.searchTextValue = newText);

        this.invokeDebouncedSearch(newText);
    }

    private invokeDebouncedSearch(searchText: string): void {
        clearTimeout(this.timeoutReference);

        this.timeoutReference = window.setTimeout(
            () => {
                if (this.state.isSearchingPortal)
                    this.invokePortalPatientSearchDebounced(searchText);
                else
                    this.invokeMylePatientSearchDebounced(searchText);
            },
            350
        );
    }

    // ------------------------------------------------------------------------------------------------------------------------
    // ------------- PORTAL PATIENT SEARCH --------------------------------------------------------------------------------------
    // ------------------------------------------------------------------------------------------------------------------------

    private async invokePortalPatientSearchDebounced(searchText: string): Promise<void> {
        const responseId = this.orderedResponseId + 1;
        this.orderedResponseId = responseId;

        let response = await Client.postAsync<any>(
            ServerOperations.Server.Portal_Proxy_SearchPatientProfiles,
            {
                IncludeLinked: this.props.isPortalSearchIncludeAlreadyLinked,
                Query: searchText
            }
        );

        if (!response.isSuccess)
            return;

        if (this.orderedResponseId === responseId)
            this.mutateState(s => s.searchPersonResults = PortalHelper.convertPortalPatientsToMylePersons(response.data.Clients));
    }

    // ------------------------------------------------------------------------------------------------------------------------
    // ------------- MYLE PATIENT SEARCH --------------------------------------------------------------------------------------
    // ------------------------------------------------------------------------------------------------------------------------

    private async invokeMylePatientSearchDebounced(searchText: string): Promise<void> {
        const responseId = this.orderedResponseId + 1;
        this.orderedResponseId = responseId;

        var mustSearchSpecificContactIds = this.props.specificContactIdsToDisplay?.length > 0;
        var SelectFirstResult = mustSearchSpecificContactIds;

        if (!mustSearchSpecificContactIds && (searchText == null || searchText.length < 2))
            return;

        let response = await Client.postAsync<SearchIndividualResponse>(
            ServerOperations.Server.Myle_SearchIndividual,
            {
                Query: searchText,
                IsInactive: this.state.isIncludingDeactivated ? this.state.isIncludingDeactivated : false,
                ResultsToReturn: this.props.maxResultsToReturn,
                QueryByPatientContactId: this.props.specificContactIdsToDisplay,
                IncludePatients: true,
                IncludeRelatives: false
            }
        );
        if (!response.isSuccess)
            return;

        if (this.orderedResponseId === responseId)
            this.processReceivedResponse_MylePatientSearch(response.data, SelectFirstResult);
    }

    private processReceivedResponse_MylePatientSearch(response: SearchIndividualResponse, SelectFirst: boolean = false): void {
        this.props.onPatientSearchResultAmountCallback?.(response.SearchIndividualResults.length);

        this.mutateState(
            state => {
                state.searchPersonResults = response.SearchIndividualResults
                    .map(result => {
                        const fullPatientPromise = this.getFullPatientPromisesFromCache(result.ContactId);
                        if (fullPatientPromise?.loadedData)
                            return fullPatientPromise.loadedData;

                        return Person.FromSearchIndividualResult(result);
                    });

                if (SelectFirst && state.searchPersonResults?.length > 0)
                    state.selectedPerson = state.searchPersonResults[0];

                // prepare search jobs
                for (let result of response.SearchIndividualResults) {
                    const fullPatientInfo = this.getFullPatientPromisesFromCache(result.ContactId);
                    if (fullPatientInfo != null)
                        continue;

                    this.addPatientToCache(result.ContactId, this.loadFullpatient(result));
                }
            },
            () => this.resumePatientLoadJob(response.OrderedResponseId)
        );
    }

    private loadFullpatient(p: SearchIndividualResult): IndividualSearchResultCachedPromise<Person> {
        let cachedPromise = new IndividualSearchResultCachedPromise<Person>();

        cachedPromise.post = async () => {
            const response = await Client.postAsync<LoadPatientModel>(ServerOperations.Server.Myle_LoadPatient, {
                IndividualId: p.ContactId,
                IncludeRelatedRecalls: false
            });

            if (!response.isSuccess)
                return null;

            const patientFull = MyleModelsPatientPatient.FromResponseData(response.data);

            if (sessionStore.clientConfiguration.HasMedfarRAMQ || cacheStore.ClinicSettings.EnableRamqGL6Service)
                await this.loadPatientRamqInfo(patientFull);
            else {
                const ramqInfo = new RAMQInfo();
                ramqInfo.Result = RamqStatus.Undefined;
                patientFull.RAMQInfo = ramqInfo;
            }

            let person = Person.FromPatient(patientFull);

            // when some promise resolve
            this.updateStateWithPatientInfo();

            return person;
        };

        return cachedPromise;
    }

    private async loadPatientRamqInfo(patient: MyleModelsPatientPatient): Promise<void> {
        const response = await Client.postAsync<RAMQInfo>(
            ServerOperations.Server.RAMQ_CheckSubscription,
            {
                Patient: {
                    IndividualID: patient.IndividualResponse.Contact_id
                },
                CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
            }
        );

        if (response.isSuccess) {
            const data = new RAMQInfo().FromResponseData(response.data);
            patient.setRamq(data);
        }
    }

    private updateStateWithPatientInfo(): void {
        // Throttle
        clearTimeout(this.timeoutReferenceUIRefresh);

        this.timeoutReferenceUIRefresh = window.setTimeout(
            () => {
                // We have it in cache. Replace it
                this.mutateState(s =>
                    s.searchPersonResults = s.searchPersonResults.map(p => this.fullPatientCacheDictionnary[p.ContactId]?.loadedData ?? p)
                );
            },
            300
        );
    }

    private addPatientToCache(contactId: string, promise: IndividualSearchResultCachedPromise<Person>): void {
        this.fullPatientCacheDictionnary[contactId] = promise;

        // set timeout callback to auto delete cached item when cache maxtime is elapsed
        window.setTimeout(() => this.removePatientFromCache(contactId), PatientAdvancedSearchControl.cacheTimeoutSeconds * 1000);
    }

    private removePatientFromCache(personContactId: string): void {
        delete this.fullPatientCacheDictionnary[personContactId];
    }

    private getFullPatientPromisesFromCache(personContactId: string): IndividualSearchResultCachedPromise<Person> {
        return this.fullPatientCacheDictionnary[personContactId];
    }

    private async resumePatientLoadJob(currentSearchId: number): Promise<void> {
        const patientsToLoad = this.state.searchPersonResults?.filter(p => p.isLightModel);
        if (patientsToLoad?.any()) {
            for (let patientToLoad of patientsToLoad) {
                const fullPatientPromise = this.fullPatientCacheDictionnary[patientToLoad.ContactId];
                await fullPatientPromise?.load();
                if (currentSearchId < this.orderedResponseId)
                    break;
            }
        }
    }
}

export { IPatientSelectCallbackFunction, PatientAdvancedSearchControl };
